import React, { useContext, useMemo } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { ThemeContext } from "../../../../constant/theme";
import { LanguageContext } from "../../../../constant/LanguageContext";
import { useAuth } from "../../../../context/AuthContext";
import text from "../../constants/reportsTranslations";
import { SkeletonBox } from "../../../../components/SkeletonLoader";
import DataTable from "../../../../components/DataTable/DataTable";
import BarChart from "../../../../components/charts/BarChart";
import AreaChart from "../../../../components/charts/AreaChart";
import ExportMenu from "../export/ExportMenu";
import EmptyState from "../states/EmptyState";
import ErrorState from "../states/ErrorState";
import { getDistributions, getSummaryCards, getTrendSeries } from "../../utils/chartDataHelpers";

const ReportPreviewPage = ({
  category, rows, summary, title, tableLoading, tableError, onRetry,
  onBack, onExcel, onPdf, onEmail, onSchedule, onExportSelected, onVisibleColumnsChange,
}) => {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const { isColumnVisible } = useAuth();
  const isDark = theme === "dark";
  const isRTL = language === "ar";
  const BackIcon = isRTL ? ArrowRight : ArrowLeft;

  const distributions = useMemo(() => getDistributions(summary), [summary]);
  const summaryCards = useMemo(() => getSummaryCards(summary), [summary]);
  const trend = useMemo(() => getTrendSeries(rows), [rows]);

  const columns = useMemo(() => {
    if (!rows.length) return [];
    const labels = text[language].fieldLabels || {};
    const tableKey = `reportsTable_${category?.id}`;
    return Object.keys(rows[0])
      .filter((k) => typeof rows[0][k] !== "object")
      .filter((k) => isColumnVisible?.("reports", tableKey, k) !== false)
      .map((k) => ({ id: k, label: labels[k] || k.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()) }));
  }, [rows, language, category?.id, isColumnVisible]);

  if (tableError) {
    return (
      <ErrorState
        theme={theme} isRTL={isRTL}
        title={text[language].errorStateTitle}
        description={text[language].errorStateDesc}
        retryLabel={text[language].errorStateRetry}
        onRetry={onRetry}
      />
    );
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, background: "none", border: "none", cursor: "pointer", color: isDark ? "#93c5fd" : "#2563eb", fontSize: 13, fontWeight: 600, flexDirection: isRTL ? "row-reverse" : "row" }}
            >
              <BackIcon size={16} /> {text[language].backToCategory}
            </button>
          )}
          <h2 style={{ fontSize: 20, fontWeight: 800, color: isDark ? "#f8fafc" : "#0f172a", margin: 0 }}>{category?.name || title}</h2>
        </div>
        <ExportMenu onExcel={onExcel} onPdf={onPdf} onEmail={onEmail} onSchedule={onSchedule} theme={theme} isRTL={isRTL} text={text} language={language} />
      </div>

      {tableLoading ? (
        <div style={{ display: "grid", gap: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
            {Array.from({ length: 4 }).map((_, i) => <SkeletonBox key={i} width="100%" height="80px" style={{ borderRadius: 14 }} />)}
          </div>
          <SkeletonBox width="100%" height="280px" style={{ borderRadius: 16 }} />
          <SkeletonBox width="100%" height="320px" style={{ borderRadius: 16 }} />
        </div>
      ) : rows.length === 0 && summaryCards.every((c) => !c.value) ? (
        <EmptyState theme={theme} isRTL={isRTL} title={text[language].emptyStateTitle} description={text[language].emptyStateDesc} />
      ) : (
        <>
          {/* Summary / Totals */}
          {summaryCards.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 24 }}>
              {summaryCards.map((c) => (
                <div key={c.key} style={{ padding: "14px 16px", borderRadius: 14, background: isDark ? "rgba(255,255,255,0.03)" : "#fff", border: `1px solid ${isDark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.06)"}` }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: isDark ? "rgba(255,255,255,0.5)" : "rgba(30,41,59,0.5)", textTransform: "capitalize", marginBottom: 6 }}>
                    {text[language].fieldLabels?.[c.key] || c.key.replace(/([A-Z])/g, " $1")}
                  </div>
                  <div style={{ fontSize: 19, fontWeight: 800, color: isDark ? "#f8fafc" : "#0f172a" }}>
                    {c.key === "dateRange" ? String(c.value).replace(" to ", " – ") : String(c.value)}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Charts */}
          {category?.id !== "company" && (
            <div style={{ marginBottom: 24 }}>
              <h3 style={sectionTitleStyle(isDark, isRTL)}>{text[language].previewCharts}</h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(340px, 100%), 1fr))", gap: 16 }}>
                <ChartCard title={text[language].previewBarChart} isDark={isDark}>
                  <BarChart data={distributions[0]?.data || []} theme={theme} color={category?.color || "#3b82f6"} noDataLabel={text[language].previewNoChartData} />
                </ChartCard>
                {trend.length > 0 && (
                  <ChartCard title={text[language].previewAreaChart} isDark={isDark}>
                    <AreaChart data={trend} theme={theme} color={category?.color || "#8b5cf6"} noDataLabel={text[language].previewNoChartData} />
                  </ChartCard>
                )}
                {category?.id !== "leave" && (
                  <ChartCard title={text[language].previewLineChart} isDark={isDark}>
                    <BarChart data={distributions[2]?.data || distributions[0]?.data || []} theme={theme} color="#10b981" noDataLabel={text[language].previewNoChartData} />
                  </ChartCard>
                )}
              </div>
            </div>
          )}

          {/* Table */}
          <div>
            <h3 style={sectionTitleStyle(isDark, isRTL)}>{text[language].previewDataTable}</h3>
            {columns.length > 0 && (
              <DataTable
                key={category?.id}
                columns={columns}
                data={rows}
                theme={theme}
                isRTL={isRTL}
                onExportSelected={onExportSelected}
                onVisibleColumnsChange={onVisibleColumnsChange}
                labels={{
                  search: text[language].tableSearch, columns: text[language].tableColumns,
                  noResults: text[language].tableNoResults,
                  selected: text[language].tableSelected, exportSelected: text[language].tableExportSelected,
                  exportSelectedExcel: text[language].tableExportSelectedExcel, exportSelectedPdf: text[language].tableExportSelectedPdf,
                  page: text[language].tablePage, of: text[language].tableOf,
                }}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

const sectionTitleStyle = (isDark, isRTL) => ({
  fontSize: 14, fontWeight: 700, marginBottom: 12,
  color: isDark ? "#e2e8f0" : "#334155", textAlign: isRTL ? "right" : "left",
});

const ChartCard = ({ title, isDark, children, wide }) => (
  <div style={{ gridColumn: wide ? "1 / -1" : "auto", padding: 16, borderRadius: 16, overflow: "hidden", background: isDark ? "rgba(255,255,255,0.02)" : "#fff", border: `1px solid ${isDark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.06)"}` }}>
    <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 8, color: isDark ? "rgba(255,255,255,0.6)" : "rgba(30,41,59,0.6)" }}>{title}</div>
    {children}
  </div>
);

export default ReportPreviewPage;
